import { ReactNode, useState, useEffect, Suspense } from 'react';
import { Autocomplete, MenuItem, SxProps } from '@mui/material';
import { TextValidator } from 'react-material-ui-form-validator';
import { useTranslation } from 'react-i18next';
import { Await } from 'react-router';
import FormFieldSkeleton from 'components/skeletons/FormField';

export interface AuturaStateFieldProps {
  label?: string;
  stateCode: string;
  setStateCode: (stateCode: string) => void;
  variant?: 'outlined' | 'standard' | 'filled';
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  resolve?: Promise<any>;
  margin?: 'dense' | 'normal' | 'none';
  sx?: SxProps;
  disableUnderline?: boolean;
}

const STATE_CODES = [
  // Mexico
  'AGU',
  'BCN',
  'BCS',
  'CAM',
  'CHP',
  'CHH',
  'COA',
  'COL',
  'DUR',
  'GUA',
  'GRO',
  'HID',
  'JAL',
  'MEX',
  'MIC',
  'MOR',
  'NAY',
  'NLE',
  'OAX',
  'PUE',
  'QUE',
  'ROO',
  'SLP',
  'SIN',
  'SON',
  'TAB',
  'TAM',
  'TLA',
  'VER',
  'YUC',
  'ZAC',
  // Canada
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'ON',
  'PE',
  'QC',
  'SK',
  'NT',
  'NU',
  'YT',
  // USA
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'VI',
  'WA',
  'WV',
  'WI',
  'WY',

  '',
];

const AuturaStateField = ({
  label,
  stateCode,
  setStateCode,
  required = false,
  disabled = false,
  variant = 'outlined',
  margin = 'none',
  readonly = false,
  resolve,
  disableUnderline = false,
  sx,
}: AuturaStateFieldProps): ReactNode => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(stateCode);

  // Sync local inputValue when the parent prop changes (e.g. from SET_LOCATION)
  useEffect(() => {
    setInputValue(stateCode);
  }, [stateCode]);

  useEffect(() => {
    // this makes sure that if the input value is a valid state code, it will be set so the user can tab without clicking
    const match = STATE_CODES.find((code) => code === inputValue);
    if (inputValue && inputValue.length > 0 && match && match !== stateCode) {
      setStateCode(match);
    }
  }, [inputValue]);

  return (
    <Suspense
      fallback={
        <FormFieldSkeleton
          initialValue={stateCode}
          label={label}
          variant={variant}
          margin={margin}
          required={required}
        />
      }
    >
      <Await resolve={resolve}>
        <Autocomplete
          disablePortal
          fullWidth
          blurOnSelect
          sx={sx}
          size={'medium'}
          disabled={disabled}
          options={STATE_CODES}
          value={stateCode}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue.toUpperCase());
          }}
          onChange={(e, value) => {
            setStateCode(value || '');
          }}
          renderOption={(props, option) => {
            return (
              <MenuItem value={option} {...props} key={option} divider>
                {option}
              </MenuItem>
            );
          }}
          renderInput={(params) => (
            <TextValidator
              {...params}
              name='state'
              label={`${label}${required ? ' *' : ''}`}
              variant={variant}
              margin={margin}
              value={stateCode}
              validators={required ? ['required'] : []}
              errorMessages={[
                t('errors.required', {
                  name: label || t('field'),
                }),
              ]}
              InputProps={{
                ...params.InputProps,
                readOnly: readonly,
                disableUnderline: disableUnderline,
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  ...params.inputProps.style,
                  cursor: readonly ? 'default' : 'text',
                  textTransform: 'uppercase',
                },
              }}
            />
          )}
        />
      </Await>
    </Suspense>
  );
};

export default AuturaStateField;
